import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { IMAGES, WHATSAPP_URL } from "@/lib/constants";
import { useLanguage } from "@/lib/i18n";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const EASE = [0.22, 1, 0.36, 1];

const LAYOUT = [
  { src: IMAGES.soggiorno, cls: "col-span-2 row-span-2" },
  { src: IMAGES.esterno, cls: "col-span-2" },
  { src: IMAGES.pranzo, cls: "col-span-2" },
  { src: IMAGES.cucina, cls: "col-span-1" },
  { src: IMAGES.bagnoVasca, cls: "col-span-1" },
  { src: IMAGES.camera, cls: "col-span-2" },
  { src: IMAGES.bagnoLavabo, cls: "col-span-2" },
];

export default function Gallery() {
  const [selected, setSelected] = useState(null);
  const { t } = useLanguage();

  const onSpotMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--spot-x", `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty("--spot-y", `${e.clientY - rect.top}px`);
  };

  return (
    <section id="galleria" data-testid="gallery-section" className="py-20 lg:py-28">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-terracotta font-bold mb-4"
            >
              {t.gallery.overline}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-ink text-balance"
            >
              {t.gallery.titleA} <em className="text-terracotta">{t.gallery.titleEm}</em> {t.gallery.titleB}
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-mono-label text-[11px] uppercase tracking-[0.2em] text-clay"
          >
            {t.gallery.hint}
          </motion.p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[150px] sm:auto-rows-[190px] lg:auto-rows-[210px] gap-3 sm:gap-4">
          {LAYOUT.map((tile, i) => {
            const caption = t.gallery.captions[i];
            return (
              <motion.button
                key={tile.src}
                data-testid={`gallery-tile-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
                onClick={() => setSelected({ src: tile.src, caption })}
                onMouseMove={onSpotMove}
                className={`spotlight-card group relative rounded-2xl overflow-hidden text-left ${tile.cls}`}
                aria-label={`${t.gallery.enlarge}: ${caption}`}
              >
                <img
                  src={tile.src}
                  alt={caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/70 to-transparent p-4 pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-linen text-xs sm:text-sm font-medium">{caption}</span>
                </span>
              </motion.button>
            );
          })}

          <motion.a
            data-testid="gallery-booking-tile"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.4, ease: EASE }}
            className="group col-span-2 rounded-2xl bg-terracotta text-linen p-6 flex flex-col justify-between overflow-hidden relative"
          >
            <ArrowUpRight className="w-6 h-6 absolute top-5 right-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            <WhatsAppIcon className="w-8 h-8 opacity-90" />
            <div>
              <p className="font-serif text-xl sm:text-2xl font-semibold leading-tight">
                {t.gallery.ctaTitle}
              </p>
              <p className="mt-1 font-mono-label text-[10px] uppercase tracking-[0.2em] text-linen/80">
                {t.gallery.ctaSub}
              </p>
            </div>
          </motion.a>
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-ink/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setSelected(null)}
            role="dialog"
            aria-modal="true"
            aria-label={selected.caption}
          >
            <motion.figure
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selected.src}
                alt={selected.caption}
                className="w-full max-h-[75vh] object-cover rounded-2xl shadow-2xl"
              />
              <figcaption className="mt-4 flex items-center justify-between">
                <span className="text-linen/90 font-serif italic text-lg">{selected.caption}</span>
                <button
                  data-testid="lightbox-close-btn"
                  onClick={() => setSelected(null)}
                  className="inline-flex items-center gap-2 rounded-full border border-linen/30 text-linen px-4 py-2 text-xs font-mono-label uppercase tracking-[0.2em] hover:bg-linen hover:text-ink transition-colors"
                  aria-label={t.gallery.close}
                >
                  <X className="w-3.5 h-3.5" /> {t.gallery.close}
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
